'use client';

import * as React from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { GameCardData } from '@/features/games/internal/types/game-card-data';
import { GameCard } from '@/features/games/shared/components/game-card';
import { useRouter } from '@/i18n/routing';

export const GameCardList = ({
  title,
  subtitle,
  games,
  imageSize = 'square',
  showPrice = false,
  showSubtitle = false,
  mainText = '',
  buttonText = '',
  buttonHref = '',
}: {
  title: string;
  subtitle?: string;
  games: GameCardData[];
  imageSize?: 'tall' | 'wide' | 'square';
  showPrice?: boolean;
  showSubtitle?: boolean;
  isMember?: boolean;
  mainText?: string;
  buttonText?: string;
  buttonHref?: string;
}) => {
  const router = useRouter();
  const [emblaRef] = useEmblaCarousel({
    loop: false,
    axis: 'x',
    align: 'start',
    containScroll: 'keepSnaps',
  });

  if (!games?.length) return null;

  const handleButtonClick = () => {
    if (!buttonHref) return;
    router.push(buttonHref);
  };

  return (
    <section aria-labelledby='games-list-title' className='mb-14'>
      <div className='mb-6'>
        <h2 id='games-list-title' className='text-text text-3xl font-semibold'>
          {title}
        </h2>
        {subtitle ? <p className='text-text-muted mt-1'>{subtitle}</p> : null}
      </div>

      <div className='overflow-hidden' ref={emblaRef} aria-roledescription={'carousel'}>
        <div className='flex snap-x snap-mandatory flex-nowrap gap-5 pt-2'>
          {games.map((game) => (
            <div
              key={game.id}
              className='max-w-[300px] flex-none shrink-0 basis-[calc((100%-24px)/2)] snap-start sm:flex-[0_0_calc((100%-24px*2)/3)] md:flex-[0_0_calc((100%-24px*3)/4)] lg:flex-[0_0_calc((100%-24px*4)/5)]'
            >
              <div className='relative top-0 h-full origin-center transition-[top] duration-200 ease-out focus-within:-top-1 hover:-top-1 motion-reduce:top-0'>
                <div className='border-border-hover bg-background-contrast h-full overflow-hidden rounded-2xl border'>
                  <GameCard
                    game={game}
                    prefetchMode='hover'
                    imageSize={imageSize}
                    showCategory={false}
                    showPrice={showPrice}
                    showSubtitle={showSubtitle}
                    overlay={{
                      ariaLabel: 'Membership promotion',
                      content: (
                        <>
                          <p className='text-text mb-3 text-base'>{mainText}</p>
                          <button
                            onClick={() => handleButtonClick()}
                            className='bg-success text-text rounded-sm px-6 py-2 text-lg font-bold whitespace-nowrap'
                          >
                            {buttonText}
                          </button>
                        </>
                      ),
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
